import LegalPageLayout from "@/components/LegalPageLayout";

const RefundPolicyPage = () => (
  <LegalPageLayout title="Refund & Cancellation Policy" lastUpdated="April 2026" testId="refund-policy-page">
    <p>
      This Refund &amp; Cancellation Policy (the &ldquo;<strong>Policy</strong>&rdquo;) explains the circumstances under which
      <strong> Rev &amp; Dev Children Clinic</strong> (operating the <strong>tots360</strong> platform) will process
      cancellations and refunds for purchases made through the Platform. Please read this Policy carefully before making a
      purchase. By completing a transaction on tots360, you acknowledge that you have read, understood and agreed to this Policy.
    </p>

    <h2>1. Nature of the Product</h2>
    <p>
      tots360 sells non-physical, digital <strong>report credits</strong>. Each credit entitles the purchaser to generate one
      AI-assisted PDF report using either the BoneAH or NIN-NutriGuide module. Because the product is digital and consumable on use,
      refunds are limited and subject to the conditions below.
    </p>

    <h2>2. Cancellation of a Purchase</h2>
    <ul>
      <li>
        <strong>Before any credit is consumed:</strong> You may request a cancellation and full refund within
        <strong> 48 hours</strong> of the transaction, provided <strong>none</strong> of the credits purchased has been used to generate a report.
      </li>
      <li>
        <strong>After a credit has been consumed:</strong> Consumed credits (i.e., those against which a PDF report has been
        generated or downloaded) are <strong>non-refundable</strong>. Unused credits in the same transaction may still be refunded
        on a pro-rata basis within the 48-hour window.
      </li>
      <li>
        To cancel, email us at <a href="mailto:info@tots360.in">info@tots360.in</a> from the registered email address with your
        transaction ID, date of purchase and reason for cancellation.
      </li>
    </ul>

    <h2>3. Grounds for a Refund</h2>
    <p>We process refunds in the following situations:</p>
    <ul>
      <li>
        <strong>Duplicate or wrong charge:</strong> If you were charged more than once for the same transaction, or the amount
        debited differs from the amount shown on the checkout page.
      </li>
      <li>
        <strong>Failed transaction with amount debited:</strong> If money was deducted from your account but no credits were
        added to your tots360 account, we will credit the value back (or refund it) after verification with the payment gateway.
      </li>
      <li>
        <strong>Service failure on our side:</strong> If a credit was consumed but we were unable, due to a technical error on
        our side, to generate or deliver the PDF report, we will regenerate the report or, at your option, restore the credit.
      </li>
      <li>
        <strong>Unused credits within 48 hours:</strong> As described in Section 2.
      </li>
    </ul>

    <h2>4. Situations where refunds will NOT be granted</h2>
    <ul>
      <li>You changed your mind after consuming a credit.</li>
      <li>The report was generated successfully but you disagree with its AI-derived output. AI reports are informational and do not constitute medical advice, as stated in our <a href="/terms">Terms &amp; Conditions</a>.</li>
      <li>Inability to use the Service due to your internet connection, device, browser incompatibility, or blocked card.</li>
      <li>Violation of our <a href="/terms">Terms &amp; Conditions</a> leading to suspension of your account.</li>
      <li>Discounted or promotional credits, unless required by law.</li>
    </ul>

    <h2>5. Refund Processing Timeline</h2>
    <ul>
      <li>Eligible refund requests are reviewed within <strong>2–3 business days</strong> of receiving your request.</li>
      <li>Once approved, the refund is initiated to the <strong>original payment method</strong>.</li>
      <li>Funds typically reflect in your account within <strong>5–7 business days</strong> for Razorpay (India) transactions and within <strong>5–10 business days</strong> for Stripe (international) transactions, subject to your bank&rsquo;s processing time.</li>
      <li>GST and payment-gateway fees, where applicable, are included in the refunded amount.</li>
    </ul>

    <h2>6. How to Request a Refund</h2>
    <ol>
      <li>Send an email to <a href="mailto:info@tots360.in">info@tots360.in</a> from your registered email address.</li>
      <li>Use subject line: <strong>&ldquo;Refund Request — [Transaction ID]&rdquo;</strong>.</li>
      <li>Include the following details in the body of the email:
        <ul>
          <li>Full name on the account</li>
          <li>Registered email and phone number</li>
          <li>Transaction ID / Order ID (from your payment receipt)</li>
          <li>Date and amount of the transaction</li>
          <li>Reason for the refund</li>
        </ul>
      </li>
      <li>We will acknowledge your request within 24 hours and keep you informed of the decision and processing status by email.</li>
    </ol>

    <h2>7. Chargebacks &amp; Disputes</h2>
    <p>
      Before initiating a chargeback with your bank or card issuer, we request that you contact us first so that we may attempt to
      resolve the issue directly. Unjustified chargebacks may result in suspension of your account and a permanent ban from
      the Platform. If a chargeback is raised fraudulently, we reserve the right to contest it with the payment gateway and to
      pursue recovery of any amount so charged.
    </p>

    <h2>8. Cancellation of Subscription-Style Plans (Future)</h2>
    <p>
      If and when tots360 introduces subscription-style plans, you will be able to cancel at any time by visiting your account
      dashboard. Cancellations will take effect at the end of the current billing period. No pro-rata refund will be issued for
      partial periods unless expressly stated.
    </p>

    <h2>9. Changes to this Policy</h2>
    <p>
      We may update this Policy periodically to reflect changes in law, business practice or customer feedback. The &ldquo;Last
      updated&rdquo; date at the top of this page will always reflect the latest revision. Material changes will be communicated
      via email or an in-app notice.
    </p>

    <h2>10. Contact Us</h2>
    <p>
      For any question related to this Policy or a specific refund request, please contact:
    </p>
    <ul>
      <li><strong>Email:</strong> <a href="mailto:info@tots360.in">info@tots360.in</a></li>
      <li><strong>Phone:</strong> +91 89857 09090 / +91 89857 09595 (Mon–Sat, 10:00–18:00 IST)</li>
      <li><strong>Postal address:</strong> Rev &amp; Dev Children Clinic, 2nd Floor, Fairmount Fortune One, Czech Colony, Sanath Nagar, Hyderabad — 500018, Telangana, India</li>
    </ul>
  </LegalPageLayout>
);

export default RefundPolicyPage;
